import React, {useEffect, useMemo, useState} from "react";
import {useHistory, useLocation, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {BackButton} from "components/BackButton";
import {Layout} from "components/Layout";
import {ConfirmModal} from "components/ConfirmModal";
import {useFetchPupilsByTargetGroup} from "hooks/useQuery/useFetchPupilsByTargetGroup";
import {removePupilFromTargetGroup} from "api/targetGroups/removePupilFromTargetGroup";
import {AddPupilsToTargetGroupModal} from "./AddPupilsToTargetGroupModal";
import {getSchools} from "redux/actions/setSchoolActions";

export const TargetGroupLvl2Details = () => {
    const { groupId, parentGroupId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { schools } = useSelector((state) => state.school);

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const schoolIdFromQuery = searchParams.get("schoolId");
    const { group, pupils, refetchPupils } = useFetchPupilsByTargetGroup(groupId);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [pupilToRemove, setPupilToRemove] = useState(null);

    useEffect(() => {
        if (!schools?.length) {
            dispatch(getSchools());
        }
    }, [dispatch, schools]);

    const effectiveSchoolId = group?.school || schoolIdFromQuery || "";

    const removeMutation = useMutation({
        mutationFn: ({ pupilId }) => removePupilFromTargetGroup(pupilId, groupId),
        onSuccess: async () => {
            await refetchPupils();
            await queryClient.invalidateQueries({ queryKey: ["targetGroups/level2", parentGroupId, effectiveSchoolId || "all"] });
            await queryClient.invalidateQueries({ queryKey: ["targetGroups/level1"] });
        },
    });

    const handleRemovePupil = (event, pupilId) => {
        event.stopPropagation();
        setPupilToRemove(pupilId);
        setIsConfirmOpen(true);
    };

    const confirmRemovePupil = () => {
        if (pupilToRemove) {
            removeMutation.mutate({ pupilId: pupilToRemove });
            setPupilToRemove(null);
        }
    };

    const handlePupilClick = (pupilId) => {
        history.push(`/pupils/${pupilId}?from=riskGroups&groupId=${groupId}`);
    };

    const handleModalClose = async () => {
        setIsModalOpen(false);
        await refetchPupils();
        await queryClient.invalidateQueries({ queryKey: ["targetGroups/level2", parentGroupId, effectiveSchoolId || "all"] });
        await queryClient.invalidateQueries({ queryKey: ["targetGroups/level1"] });
    };

    return (
        <Layout>
            <div className="page">
                <header className="page__header">
                    <BackButton />
                    <p className="page__title">{group?.name || "Целевая группа"}</p>
                </header>

                <div className="school-page__wrapper">
                    <div className="list gesturedZone risk-groups__list">
                        {pupils?.map((pupil) => {
                            const school = schools.find(({ _id }) => _id === pupil.class?.school);

                            return (
                                <div
                                    key={pupil._id}
                                    className="risk-groups__list-item risk-groups__list-item--with-remove"
                                >
                                    <div
                                        onClick={() => handlePupilClick(pupil._id)}
                                        style={{ flex: 1, cursor: "pointer" }}
                                    >
                                        <p>{pupil.name} {pupil.surname}</p>
                                        <p className="risk-groups__school">
                                            {school && <span>{school.name},</span>}{" "}
                                            <span>{pupil.class?.number} {pupil.class?.letter}</span>
                                        </p>
                                    </div>

                                    <button
                                        className="risk-groups__remove-btn"
                                        onClick={(event) => handleRemovePupil(event, pupil._id)}
                                        title="Исключить из целевой группы"
                                    >
                                        −
                                    </button>
                                </div>
                            );
                        })}

                        {pupils?.length === 0 && (
                            <p style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
                                В этой целевой группе пока нет учеников
                            </p>
                        )}
                    </div>

                    <button
                        className="btn btn_primary"
                        style={{ marginTop: "2rem", width: "100%" }}
                        onClick={() => setIsModalOpen(true)}
                        disabled={!effectiveSchoolId}
                    >
                        Добавить учеников в ЦГ
                    </button>
                </div>

                {isModalOpen && (
                    <AddPupilsToTargetGroupModal
                        groupId={groupId}
                        groupSchoolId={effectiveSchoolId}
                        onClose={handleModalClose}
                    />
                )}

                <ConfirmModal
                    isOpen={isConfirmOpen}
                    onClose={() => setIsConfirmOpen(false)}
                    onConfirm={confirmRemovePupil}
                    title="Исключить ученика"
                    message="Вы уверены, что хотите исключить ученика из целевой группы?"
                    confirmText="Исключить"
                    cancelText="Отмена"
                />
            </div>
        </Layout>
    );
};
